import { BuildingDesign } from '@shared/models/design/building-design.model';
import { SemiPartial } from '@shared/models/type-helpers.model';
import { Storey } from '@shared/models/design/storey.model';
import { mutateStoreyActivityReference } from './storey-activity-reference.mutator';
import {
  getBuildingContainingStorey,
  getBuildingStoreys,
  getStoreyById,
  getStoreyIndex,
  setBuildingStoreys,
  StoreySelector,
} from '../storey.helpers';
import { ArraySplice } from 'type-fest';
import { mutateStoreys } from './storeys.mutator';
import { Point2D } from '@shared/models/geometry.model';
import { shallowEqualMap } from '@shared/helpers/array.helpers';
import { mutateStorey } from './storey.mutator';

/**
 * Apply updates to a list of storeys in a design
 * @param design - The design to update
 * @param updates - The updates to apply to the storeys
 * @returns The updated design (if any changes were made)
 */
const updateStoreysInDesign = (
  design: BuildingDesign,
  ...updates: SemiPartial<Storey, 'id'>[]
): BuildingDesign =>
  storeysMutatorFnFactory(mutateStoreys.update)(design, ...updates);

/**
 * Run a storey-list mutator on every {@link Building}. Id-targeted mutators
 * no-op on buildings that do not contain the id, so shape updates on building
 * 2 no longer fall back to `buildings[0]`.
 */
export const storeysMutatorFnFactory = <
  T extends (storeys: Storey[], ...args: any[]) => Storey[],
>(
  mutatorFn: T,
): ((
  design: BuildingDesign,
  ...args: ArraySplice<Parameters<T>, 0, 1>
) => BuildingDesign) => {
  return (design, ...args) => {
    let changed = false;
    const buildings = design.buildings.map((building) => {
      const nextStoreys = mutatorFn(building.storeys, ...args);
      if (nextStoreys === building.storeys) return building;
      changed = true;
      return { ...building, storeys: nextStoreys };
    });
    return changed ? { ...design, buildings } : design;
  };
};

// /**
//  * Create a shape mutator function that mutates BuildingDesign with shape changes
//  * @param mutatorFn - The mutator function to use
//  * @returns A function that mutates a shape in a storey in a design
//  */
// const shapeMutatorFnFactory = <
//   T extends (shape: Shape, ...args: any[]) => Shape,
// >(
//   mutatorFn: T,
// ): ((
//   design: BuildingDesign,
//   storeyOrId: ItemOrItemId<Storey>,
//   shapeOrId: ItemOrItemId<Shape>,
//   ...args: ArraySplice<Parameters<T>, 0, 1> // Remove the first argument (the shape)
// ) => BuildingDesign) => {
//   return (design, storeyOrId, shapeOrId, ...args) => {
//     const storey = getStoreyById(design, storeyOrId);
//     const id = getId(shapeOrId);

//     if (!storey) {
//       console.warn(`Shape with id ${id} not found in storey ${storeyOrId}`);
//       return design;
//     }
//     return updateStoreysInDesign(design, {
//       ...storey,
//       shapes: storey.shapes.map((s) =>
//         s.id === id ? mutatorFn(s, ...args) : s,
//       ),
//     });
//   };
// };

/**
 * Create a storey activity reference mutator that mutates BuildingDesign with activity reference changes
 * @param mutatorFn - The mutator function to use
 * @returns A function that mutates activity references in a storey in a design
 */
const activityReferenceMutatorFnFactory = <
  T extends (storey: Storey, ...args: any[]) => Storey,
>(
  mutatorFn: T,
): ((
  design: BuildingDesign,
  storeySelector: StoreySelector,
  ...args: ArraySplice<Parameters<T>, 0, 1>
) => BuildingDesign) => {
  return (design, storeySelector, ...args) => {
    const storey = getStoreyById(design, storeySelector);
    if (!storey) return design;

    // Avoid triggering needless updates
    const updatedStorey = mutatorFn(storey, ...args);
    if (updatedStorey === storey) return design;

    return updateStoreysInDesign(design, updatedStorey);
  };
};

type StoreyActivityRefAddArgs = ArraySplice<
  Parameters<typeof mutateStoreyActivityReference.add>,
  0,
  1
>;
type StoreyActivityRefRemoveArgs = ArraySplice<
  Parameters<typeof mutateStoreyActivityReference.remove>,
  0,
  1
>;
type StoreyActivityRefUpdateArgs = ArraySplice<
  Parameters<typeof mutateStoreyActivityReference.update>,
  0,
  1
>;

const addStoreyActivityRef = (
  storey: Storey,
  ...args: StoreyActivityRefAddArgs
) => mutateStoreyActivityReference.add(storey, ...args);

const removeStoreyActivityRef = (
  storey: Storey,
  ...args: StoreyActivityRefRemoveArgs
) => mutateStoreyActivityReference.remove(storey, ...args);

const updateStoreyActivityRef = (
  storey: Storey,
  ...args: StoreyActivityRefUpdateArgs
) => mutateStoreyActivityReference.update(storey, ...args);

type RemoveAllPointsOptions = {
  /**
   * The storey to remove the points from. If not provided, all points will be removed from all storeys.
   */
  deleteInStoreysAbove?: Storey;
};

/**
 * Remove all points at a position in all shapes in all storeys in a design
 * @param design - The design to remove the points from
 * @param position - The position to remove the points at
 * @returns The updated design (if any changes were made)
 */
const removeAllPointsAt = (
  design: BuildingDesign,
  position: Point2D,
  { deleteInStoreysAbove }: RemoveAllPointsOptions = {},
): BuildingDesign => {
  const applyFromIndex = (storeys: Storey[], fromIndex: number): Storey[] =>
    shallowEqualMap(storeys, (storey, index) =>
      index >= fromIndex
        ? mutateStorey.many.points.removeAt(storey, position)
        : storey,
    );

  if (deleteInStoreysAbove) {
    const targetBuilding = getBuildingContainingStorey(
      design,
      deleteInStoreysAbove.id,
    );
    if (!targetBuilding) return design;
    const storeyIndex = getStoreyIndex(design, deleteInStoreysAbove);
    const nextStoreys = applyFromIndex(targetBuilding.storeys, storeyIndex);
    return setBuildingStoreys(design, targetBuilding.id, nextStoreys);
  }

  let changed = false;
  const buildings = design.buildings.map((building) => {
    const nextStoreys = applyFromIndex(building.storeys, 0);
    if (nextStoreys === building.storeys) return building;
    changed = true;
    return { ...building, storeys: nextStoreys };
  });
  return changed ? { ...design, buildings } : design;
};

/**
 * Helper to mutate a design by adding/removing/updating activities and storeys
 */
export const mutateStoreyInDesign = {
  /**
   * Add a storey to a building in the design.
   * @param design - The design to update
   * @param buildingId - Building to add the storey to; omitted = first building
   * @returns The updated design (if any changes were made)
   */
  add: (design: BuildingDesign, buildingId?: string): BuildingDesign => {
    const prevStoreys = getBuildingStoreys(design, buildingId);
    const nextStoreys = mutateStoreys.add(prevStoreys);
    if (nextStoreys === prevStoreys) return design;
    return setBuildingStoreys(design, buildingId, nextStoreys);
  },

  /**
   * Remove a storey from the design
   * @param design - The design to update
   * @returns The updated design (if any changes were made)
   */
  remove: storeysMutatorFnFactory(mutateStoreys.remove),

  /**
   * Update a storey in the design
   * @param design - The design to update
   * @param updates - The updates to apply to the storeys
   * @returns The updated design (if any changes were made)
   */
  update: storeysMutatorFnFactory(mutateStoreys.update),

  /**
   * Mutations involving shapes
   */
  shape: {
    /**
     * Add a shape to a storey in a design
     * @param design - The design to update
     * @param storeyOrId - The storey or id of the storey to add the shape to
     * @param shape - The shape to add
     * @returns The updated design
     */
    add: storeysMutatorFnFactory(mutateStoreys.shape.add),

    /**
     * Remove a shape from a storey in a design
     * @param design - The design to update
     * @param storeyOrId - The storey or id of the storey to remove the shape from
     * @param shapeOrId - The shape or id of the shape to remove
     * @returns The updated design (if any changes were made)
     */
    remove: storeysMutatorFnFactory(mutateStoreys.shape.remove),

    /**
     * Update a shape in a storey in a design
     * @param design - The design to update
     * @param storeyOrId - The storey or id of the storey to update the shape in
     * @param shapeOrId - The shape or id of the shape to update
     * @param update - Changes to apply to the shape
     * @returns The updated design (if any changes were made)
     */
    update: storeysMutatorFnFactory(mutateStoreys.shape.update),

    /**
     * Mutations involving points in a shape
     */
    point: {
      /**
       * Set the points of a shape by completely replacing the existing points
       * @param design - The design to update
       * @param storeyOrId - The storey or id of the storey to set the points of
       * @param shapeOrId - The shape or id of the shape to set the points of
       * @param points - The points to set
       * @returns The updated design
       */
      set: storeysMutatorFnFactory(mutateStoreys.shape.point.set),

      /**
       * Add one or more points to a shape by adding them to the end of the existing points
       * @param design - The design to update
       * @param storeyOrId - The storey or id of the storey to add the points to
       * @param shapeOrId - The shape or id of the shape to add the points to
       * @param points - The points to add
       * @returns The updated design
       */
      add: storeysMutatorFnFactory(mutateStoreys.shape.point.add),

      /**
       * Insert one or more points at a specific index in a shape
       * @param design - The design to update
       * @param storeyOrId - The storey or id of the storey to insert the points at
       * @param shapeOrId - The shape or id of the shape to insert the points at
       * @param index - The index to insert the points at
       * @param points - The points to insert
       * @returns The updated design
       */
      insertAt: storeysMutatorFnFactory(mutateStoreys.shape.point.insertAt),

      /**
       * Remove a point from a shape by removing it from the existing points
       * @param design - The design to update
       * @param storeyOrId - The storey or id of the storey to remove the point from
       * @param shapeOrId - The shape or id of the shape to remove the point from
       * @param indexOrPoint - The index or point to remove
       * @returns The updated design
       */
      remove: storeysMutatorFnFactory(mutateStoreys.shape.point.remove),

      /**
       * Move a point in a shape by moving it to a new position
       * @param design - The design to update
       * @param storeyOrId - The storey or id of the storey to move the point in
       * @param shapeOrId - The shape or id of the shape to move the point in
       * @param existingIndexOrPoint - The index or point to move
       * @param newPosition - The new position to move the point to
       * @returns The updated design
       */
      move: storeysMutatorFnFactory(mutateStoreys.shape.point.move),

      /**
       * Translate multiple points in a shape by a delta
       * @param design - The design to update
       * @param storeyOrId - The storey or id of the storey to translate the points in
       * @param shapeOrId - The shape or id of the shape to translate the points in
       * @param delta - The delta to translate the points by
       * @param indexes? - The indexes of the points to translate. If not provided, all points will be translated.
       * @returns The updated design
       */
      translateMany: storeysMutatorFnFactory(
        mutateStoreys.shape.point.translateMany,
      ),
    },

    /**
     * Mutations involving many shapes and points
     */
  },
  many: {
    points: {
      /**
       * Move multiple points in all shapes in all storeys in a design
       * @param design - The design to move the points in
       * @param moveOperations - The move operations to apply
       * @returns The updated design (if any changes were made)
       */
      move: storeysMutatorFnFactory(mutateStoreys.many.points.move),

      /**
       * Remove all points at a position in all shapes in all storeys in a design
       * @param design - The design to remove the points from
       * @param position - The position to remove the points at
       * @returns The updated design (if any changes were made)
       */
      removeAt: removeAllPointsAt,
    },
    shapes: {
      /**
       * Update multiple shapes in all storeys in a design
       * @param design - The design to update the shapes in
       * @param updates - The updates to apply to the shapes
       * @returns The updated design (if any changes were made)
       */
      update: storeysMutatorFnFactory(mutateStoreys.many.shapes.update),

      /**
       * Remove multiple shapes from all storeys in a design
       * @param design - The design to remove the shapes from
       * @param shapeOrIds - The shapes or ids of the shapes to remove
       * @returns The updated design (if any changes were made)
       */
      remove: storeysMutatorFnFactory(mutateStoreys.many.shapes.remove),
    },
  },
  all: {
    points: {
      translate: storeysMutatorFnFactory(mutateStoreys.all.points.translate),
    },
  },
  activityRef: {
    /**
     * Add an activity reference to a storey in a design
     * @param design - The design to update
     * @param storeyOrId - The storey or id of the storey to add the activity reference to
     * @param activityId - Activity identifier from the schedule catalogue
     * @param type - Activity type (see SMHI usage categories)
     * @param share - Floor area share that the activity occupies in the storey
     * @returns The updated design (if any changes were made)
     */
    add: activityReferenceMutatorFnFactory(addStoreyActivityRef),

    /**
     * Remove an activity reference from a storey in a design
     * @param design - The design to update
     * @param storeyOrId - The storey or id of the storey to remove the activity reference from
     * @param activityId - Activity identifier whose reference should be removed
     * @returns The updated design (if any changes were made)
     */
    remove: activityReferenceMutatorFnFactory(removeStoreyActivityRef),

    /**
     * Update an activity reference within a storey in a design
     * @param design - The design to update
     * @param storeyOrId - The storey or id of the storey to update the activity reference in
     * @param update - Semi-partial activity reference payload
     * @returns The updated design (if any changes were made)
     */
    update: activityReferenceMutatorFnFactory(updateStoreyActivityRef),
  },
} as const;
